import { createDraftSafeSelector, createSlice, PayloadAction } from '@reduxjs/toolkit'
import { GlobalState } from '@store'
import { LoadingState, Role, User } from '@types'

interface profileState extends User {
  loadingStatus: LoadingState
  message: string
  parentName: string
}

const initialState: profileState = {
  name: '',
  isRejected: false,
  phone: '',
  city: '',
  document: '',
  files: [],
  email: '',
  role: Role.referenciador,
  loadingStatus: 'NOT_STARTED',
  message: '',
  isVerified: false,
  bank: '',
  bankDocument: '',
  isSaver: false,
  parentName: '',
  accountData: {
    accountNumber: 0,
    bankName: '',
    accountType: '',
    accountDocument: '',
    accountDocumentLink: '',
    physicalAddress: '',
    rutDocument: ''
  }
}

export type profileAction = PayloadAction<{ profile: User; parent: User }>
export type patchUserAction = PayloadAction<Partial<User>>

const profileSlice = createSlice({
  name: 'profile',
  initialState,

  reducers: {
    getProfile: state => {
      return {
        ...state,
        loadingStatus: 'LOADING'
      }
    },
    patchProfile: (state, _action: patchUserAction) => {
      return {
        ...state,
        loadingStatus: 'LOADING'
      }
    },
    profileSuccess: (state, { payload }: profileAction) => {
      const {
        name,
        document,
        files,
        email,
        phone,
        city,
        role,
        isVerified,
        bank,
        bankDocument,
        isRejected,
        accountData,
        createdAt
      } = payload.profile
      return {
        ...state,
        name,
        document,
        files,
        email,
        phone,
        city,
        role,
        loadingStatus: 'SUCCESS',
        isVerified,
        bank,
        bankDocument,
        createdAt,
        parentName: payload.parent?.name || '',
        isRejected,
        accountData: {
          accountNumber: accountData?.accountNumber || '',
          bankName: accountData?.bankName || '',
          accountType: accountData?.accountType || '',
          accountDocument: accountData?.accountDocument || '',
          accountDocumentLink: accountData?.accountDocumentLink || '',
          physicalAddress: accountData?.physicalAddress || '',
          rutDocument: accountData?.rutDocument || ''
        }
      }
    },
    profileFailed: (state, action) => {
      return {
        ...state,
        loadingStatus: 'FAILED',
        message: action.payload
      }
    },
    clearprofile: _state => {
      return initialState
    }
  }
})

export const profileLoader = createDraftSafeSelector(
  [(state: GlobalState) => state.profile.loadingStatus, (state: GlobalState) => state.profile.message],
  (loadingStatus, message) => {
    const isLoading = loadingStatus === 'LOADING'
    const isFailed = loadingStatus === 'FAILED'
    const isSuccess = loadingStatus === 'SUCCESS'
    return { isLoading, isFailed, isSuccess, message }
  }
)
export const userProfile = createDraftSafeSelector(
  [(state: GlobalState) => state.profile],
  ({
    name,
    email,
    phone,
    city,
    files,
    document,
    role,
    bank = '',
    bankDocument = '',
    parentName = '',
    isRejected = false,
    isVerified = false,
    isSaver = true,
    accountData,
    createdAt
  }) => {
    return {
      name,
      email,
      phone,
      city,
      files,
      document,
      role,
      bank,
      bankDocument,
      parentName,
      isRejected,
      isVerified,
      isSaver,
      accountData,
      createdAt
    }
  }
)

export const { getProfile, profileSuccess, profileFailed, clearprofile, patchProfile } = profileSlice.actions
export default profileSlice.reducer
